import { css } from '@emotion/react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, IconButton, Link } from '@mui/material';
import { useGlobalState } from 'providers';
import { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { shouldBePassword } from '@jebel/utils';

import { SocialButtons } from 'features/auth/components';
import { useAppAuth } from 'providers/useAppAuth';
import { DividerOr } from 'shared/components/symbols';
import { Form, FormTextField, FormCheckbox, Button, Typography } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { usePushNotifications, useQueryParams } from 'shared/hooks';
import { isValidEmail } from 'shared/utils/form';
import { recordDebug, recordError, recordUser } from 'shared/utils/record';

const formBlockCss = css`
  display: grid;
  gap: 1rem;
`;

type FormData = {
  password: string;
  email: string;
  tosPrivacy: boolean;
};

interface Params {
  accessCodeHash: string;
  email: string;
  /** @deprecated Use `to` instead. */
  isBusiness?: string;
  /**
   * Defines the page to redirect to after registration.
   * @example `/home`
   */
  to?: string;
}

export function EmailRegistrationPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [params] = useQueryParams<Params>();

  const { requestPermission } = usePushNotifications();
  const { register } = useAppAuth();
  const { push: navigate } = useHistory();
  const { toasts, helpModal } = useGlobalState();

  useEffect(() => {
    if (!params.email) {
      return navigate(ROUTES.public.login);
    }

    recordUser(params.email);
  }, [params.email]);

  const onSubmit = async ({ email, password, tosPrivacy }: FormData) => {
    if (!tosPrivacy) {
      return;
    }

    try {
      await register({ email, password, accessCodeHash: params.accessCodeHash });
      // Request permission for push notifications.
      // https://github.com/jebelapp/jebel/issues/1515
      await requestPermission();

      let to = params.to ?? ROUTES.user.settings.information;

      recordDebug(`Redirecting to ${to}`);

      if (params.isBusiness) {
        to = ROUTES.user.organizations.index;
      }

      return navigate(to);
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        toasts.showError(err.message);
      }
    }
  };

  const handleOpenNeedHelp = () => {
    helpModal.open({
      subject: 'Need help creating my password',
      email: params.email,
    });
  };

  return (
    <Fragment>
      <Box display="grid" gridTemplateRows="auto auto">
        <Box display="flex" justifyContent="center" alignItems="end">
          <Typography align="center" variant="subtitle3">
            <strong>Welcome!</strong> Create your password to complete registration
          </Typography>
        </Box>
      </Box>

      <Form
        oldCss={formBlockCss}
        initialValues={{ tosPrivacy: false, email: params.email, password: '' }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values }) => (
          <Fragment>
            <FormTextField
              disabled
              inputProps={{
                color: 'primary',
                label: 'Email Address',
                variant: 'outlined',
                disabled: true,
              }}
              fieldProps={{ name: 'email', validate: isValidEmail }}
            />

            <FormTextField
              inputProps={{
                color: 'primary',
                label: 'Create Your Password',
                variant: 'outlined',
                type: showPassword ? 'text' : 'password',
                autoComplete: 'new-password',
                InputProps: {
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => {
                        setShowPassword(showPass => !showPass);
                      }}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                },
              }}
              fieldProps={{ name: 'password', validate: shouldBePassword }}
            />

            <FormCheckbox
              fieldProps={{ name: 'tosPrivacy' }}
              checkboxProps={{
                disabled: false,
                indeterminate: false,
                labelPlacement: 'end',
                label: (
                  <Typography>
                    By continuing, you agree to the &nbsp;
                    <Link target="_blank" href="/terms-of-service.html">
                      Terms of Service
                    </Link>
                    &nbsp; and &nbsp;
                    <Link target="_blank" href="/privacy-policy.html">
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                ),
              }}
            />

            <Button
              type="submit"
              size="large"
              variant="contained"
              loading={isSubmitting}
              color="primary"
              disabled={!values.tosPrivacy}
            >
              Register
            </Button>

            <DividerOr />

            <SocialButtons />

            <Link align="center" onClick={handleOpenNeedHelp}>
              Need Help?
            </Link>
          </Fragment>
        )}
      </Form>
    </Fragment>
  );
}
